import { getServiceRows, populateExpandoServiceData, ProductTile, transformServicesList } from '@ahmdigital/ui';
import { includes, isEmpty, map, omit } from 'lodash/fp';
import { PRODUCT_TYPE } from '@ahmdigital/constants';
import { ReactElement, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { CHOOSE_YOUR_COVER_TYPE_PARAM } from '../../../../../product/util/use-choose-your-cover-journey-type';
import { JOURNEY_TYPE_URL_SLUG } from '../../../../constants';
import { MovingColumn } from '../../styles';
import { ProductType, ProductTypeType } from '../../../../../../types/product';
import { RootState } from '../../../../../../reducers';
import { useByocNavigationContext } from '../../../../context/byoc-navigation-context';
import buildYourCoverActions, { BuildYourCoverProductType } from '../../../../../../actions/build-your-cover';
import getNewProductLabel from '../../../../../../utils/get-new-product-label';
import isProductNew from '../../../../../../utils/is-product-new';
import prepareProductsForProductTiles from '../../../../../../utils/prepare-products-for-product-tiles';
import selectCustomer from '../../../../../../selectors/select-customer';
import selectPrices from '../../../../../../selectors/select-prices';
import truncateFrequency from '../../../../../../utils/truncate-frequency';

type UseCarouselTilesProps = {
  products: ProductType[];
  hasProductWithFewerInclusions: boolean;
  isExpanded: boolean;
  isStuck: boolean;
  handleToggle: () => void;
};

const useCarouselTiles = ({
  products,
  hasProductWithFewerInclusions,
  isExpanded,
  isStuck,
  handleToggle,
}: UseCarouselTilesProps): ReactElement[] => {
  const dispatch = useDispatch();
  const customer = useSelector(selectCustomer);
  const prices = useSelector(selectPrices);
  const { selectedProducts } = useSelector((state: RootState) => state.buildYourCover);
  const useFutureEffectiveDate = useSelector((state: RootState) => state.price.useFutureEffectiveDate);

  const { journeyType } = useByocNavigationContext();

  const isBuildYourCoverProductType = (productType: ProductType['type']): productType is BuildYourCoverProductType =>
    productType === PRODUCT_TYPE.EXTRAS || productType === PRODUCT_TYPE.HOSPITAL;
  const productTileQueryString = `${CHOOSE_YOUR_COVER_TYPE_PARAM}=${JOURNEY_TYPE_URL_SLUG[journeyType]}`;

  const handleProductSelect = useCallback(
    (productType: ProductType['type'], productId: ProductType['id']) => {
      if (isBuildYourCoverProductType(productType)) {
        dispatch(buildYourCoverActions.setOrUnsetSelectedProduct(productType, productId));
      }
    },
    [dispatch],
  );

  if (isEmpty(products)) {
    return [];
  }

  const expandoProducts = populateExpandoServiceData(products);
  const mappedProducts = expandoProducts.map((product: ProductType) => ({
    ...omit([PRODUCT_TYPE.EXTRAS, PRODUCT_TYPE.HOSPITAL], product),
    [`${product.type}Services`]: transformServicesList(product, product.type),
  }));

  const tilesProductType = products[0]?.type as ProductTypeType;
  const allServices = getServiceRows(mappedProducts, tilesProductType);

  const treatedProducts = prepareProductsForProductTiles(products, customer, prices, useFutureEffectiveDate);

  const selectedProductId = selectedProducts[tilesProductType as keyof typeof selectedProducts];
  const productIds = map('id', products);
  if (selectedProductId && !includes(selectedProductId, productIds)) {
    handleProductSelect(tilesProductType, selectedProductId);
  }

  return treatedProducts.map((product) => {
    let customLabel: string | undefined;
    const isNewProduct = isProductNew(product?.id, product?.parentProductId, product?.type);
    if (isNewProduct) {
      customLabel = getNewProductLabel(hasProductWithFewerInclusions);
    }

    const isBestSeller = !isNewProduct && product?.isBestSeller;

    return (
      <MovingColumn key={product?.id} variant="noGutter">
        <ProductTile
          {...product}
          customLabel={customLabel}
          url={`${product?.url}?${productTileQueryString}`}
          onToggle={handleToggle}
          // @ts-expect-error [🤖 PLEASE FIX 🤖]: Type '(productType: ProductType['type'], productId: ProductType['id']) => void' is not assignable to type '(...args: unknown[]) => unknown'.
          handleSelect={handleProductSelect}
          isBestSeller={isBestSeller}
          isExpandable
          isExpanded={isExpanded}
          hasFewerInclusions={(isBestSeller || isNewProduct) && hasProductWithFewerInclusions}
          isStuck={isStuck}
          ctaIcon="plus-circle"
          ctaText="Add cover"
          scale={product?.scale}
          selectedProductId={selectedProductId ?? undefined}
          services={allServices}
          frequency={truncateFrequency(customer.frequency)}
          price={product?.price}
        />
      </MovingColumn>
    );
  });
};

export default useCarouselTiles;
